/**
 * Centralized event name constants and property shape hints.
 *
 * Use these constants instead of inline string literals so event names stay
 * consistent across the codebase and renames are safe (grep + compile).
 *
 * Mixpanel best practice: use `object_verb` snake_case names.
 */
export const AnalyticsEvents = {
   // Top of funnel
   PAGE_VIEW: 'page_view',
   LANDING_VIEWED: 'landing_viewed',
   LANDING_GUIDE_CARD_CLICKED: 'landing_guide_card_clicked',
   LANDING_PLANNER_CARD_CLICKED: 'landing_planner_card_clicked',
   LANDING_HERO_VIDEO_PLAYED: 'landing_hero_video_played',
   // Org landing engagement. `layout` carries the LD landing-layout variant on
   // interaction events; every event also gets the flag as a super property
   // via useLDFlagSync, so variant breakdowns work across the whole funnel.
   LANDING_SECTION_VIEWED: 'landing_section_viewed',
   LANDING_PROGRAM_SWITCHED: 'landing_program_switched',
   LANDING_TRACK_PRICES_CLICKED: 'landing_track_prices_clicked',
   LANDING_FAQ_EXPANDED: 'landing_faq_expanded',
   LANDING_IMPACT_CTA_CLICKED: 'landing_impact_cta_clicked',
   LANDING_EVENT_CARD_CLICKED: 'landing_event_card_clicked',
   // NPT-1800 — a row of the youth-sports schedule card, which deep-links into
   // the planner with that event open. Replaces the card's planner CTA there.
   LANDING_SCHEDULE_EVENT_CLICKED: 'landing_schedule_event_clicked',

   // Season planner
   PLANNER_VIEWED: 'planner_viewed',
   PLANNER_PROGRAM_SWITCHED: 'planner_program_switched',
   PLANNER_SEARCH_OPENED: 'planner_search_opened',
   PLANNER_GAME_STATUS_CHANGED: 'planner_game_status_changed',
   PLANNER_TRACK_PRICES_CLICKED: 'planner_track_prices_clicked',
   PLANNER_GUIDE_CLICKED: 'planner_guide_clicked',
   PLANNER_TRIP_ATTACHED: 'planner_trip_attached',
   // The browse panel, end to end. Between planner_search_opened and the
   // reserve there used to be nothing at all, which is where the whole
   // youth-sports experience lives.
   PLANNER_HOTELS_VIEWED: 'planner_hotels_viewed',
   PLANNER_ROOMS_VIEWED: 'planner_rooms_viewed',
   PLANNER_ROOM_MODAL_OPENED: 'planner_room_modal_opened',
   PLANNER_HOTEL_OPENED: 'planner_hotel_opened',
   PLANNER_HOTEL_SELECTED: 'planner_hotel_selected',
   PLANNER_VIEW_ALL_HOTELS: 'planner_view_all_hotels',
   PLANNER_BOOK_CLICKED: 'planner_book_clicked',
   /** A plan write failed — the traveler's choice did not stick. */
   PLANNER_ACTION_FAILED: 'planner_action_failed',

   // Game guides
   GUIDE_VIEWED: 'guide_viewed',
   GUIDE_HOTELS_CTA_CLICKED: 'guide_hotels_cta_clicked',
   GUIDE_PLACE_CLICKED: 'guide_place_clicked',
   GUIDE_TRACK_PRICES_CLICKED: 'guide_track_prices_clicked',

   // Auth
   SIGNUP_STARTED: 'signup_started',
   SIGNUP_COMPLETED: 'signup_completed',
   LOGIN_STARTED: 'login_started',
   // The hop to WorkOS AuthKit and back (NPT-1694) — see src/analytics/authHandoff.ts.
   AUTH_HANDOFF_STARTED: 'auth_handoff_started',
   AUTH_HANDOFF_RETURNED: 'auth_handoff_returned',

   // Hotel discovery
   HOTEL_SEARCH_SUBMITTED: 'hotel_search_submitted',
   HOTEL_SEARCH_RESULTS_VIEWED: 'hotel_search_results_viewed',
   HOTEL_SEARCH_FILTER_CHANGED: 'hotel_search_filter_changed',
   LOCATION_SUGGEST_NO_RESULTS: 'location_suggest_no_results',
   // Sort dropdown change on the results page. `sortType` also rides along on
   // hotel_search_results_viewed so funnel conversion can be segmented by the
   // active sort — the tuning signal for the Recommended algorithm.
   HOTEL_SEARCH_SORT_CHANGED: 'hotel_search_sort_changed',
   HOTEL_VIEWED: 'hotel_viewed',
   HOTEL_RATE_SELECTED: 'hotel_rate_selected',

   // Map search v2 (NPT-2071). `trigger` on map_viewport_search is pan | zoom | search_this_area
   // | initial | reset | event | zoom_out; `truncated` = more hotels in view than were returned.
   MAP_VIEWPORT_SEARCH: 'map_viewport_search',
   MAP_PIN_CLICK: 'map_pin_click',
   MAP_CARD_TO_DETAIL: 'map_card_to_detail',
   EVENT_PIN_CLICK: 'event_pin_click',
   EVENT_OFFSCREEN_CHIP_CLICK: 'event_offscreen_chip_click',
   MAP_SORT_CHANGED: 'map_sort_changed',
   MAP_FILTER_CHANGED: 'map_filter_changed',

   // Search bar chip row + tabs
   SEARCH_CHIP_ROW_VIEWED: 'search_chip_row_viewed',
   RECENT_SEARCH_CLICKED: 'recent_search_clicked',
   RECENT_SEARCH_REMOVED: 'recent_search_removed',
   RECENT_SEARCHES_CLEARED: 'recent_searches_cleared',
   TRENDING_DESTINATION_CLICKED: 'trending_destination_clicked',
   SEARCH_EVENTS_TAB_CLICKED: 'search_events_tab_clicked',
   // NPT-1848: match free search with games/schedules within database
   EVENT_MATCH_PROMPT_VIEWED: 'event_match_prompt_viewed',
   EVENT_MATCH_PROMPT_ACCEPTED: 'event_match_prompt_accepted',
   EVENT_MATCH_PROMPT_DISMISSED: 'event_match_prompt_dismissed',

   // Booking funnel
   PREBOOK_STARTED: 'prebook_started',
   PREBOOK_FAILED: 'prebook_failed',
   // NPT-2117 — fires ONCE per checkout load, and only once the prebook succeeded and the form is
   // on screen (`prebookOutcome: 'success'`). It used to fire four times per load (once per
   // stepName) and also after a failed prebook, so travellers who only ever saw a rate-expired
   // modal counted as having reached the form.
   BOOKING_STEP_VIEWED: 'booking_step_viewed',
   BOOKING_STEP_COMPLETED: 'booking_step_completed',
   BOOKING_PROGRAM_SELECTED: 'booking_program_selected',
   // NPT-2117 — every press of Complete Booking (and every Enter-key submit), BEFORE validation.
   // `booking_submit_clicked` below only fires once yup passes, so a click that fails validation
   // never counted as a submit; the two together give "clicked" vs "submitted a valid form".
   BOOKING_SUBMIT_ATTEMPTED: 'booking_submit_attempted',
   /** A Complete Booking press that passed field validation (kept for the existing funnels). */
   BOOKING_SUBMIT_CLICKED: 'booking_submit_clicked',
   BOOKING_FORM_VALIDATION_FAILED: 'booking_form_validation_failed',
   // NPT-2117 — the traveller left checkout without booking: on unmount or pagehide, with
   // secondsOnPage, lastSectionTouched (contact / traveler / card / billing / program / coupon /
   // agreement / nonrefundable / refundable_upgrade / booking_for), hostedCardFormOpen,
   // refundabilityKind and exitVia (back / leave_dialog / tab_close / navigation / backgrounded).
   // Can fire more than once per visit when the tab is backgrounded and resumed; the last one counts.
   // Browsers hide a tab before `pagehide`, so a tab close usually reports as 'backgrounded' too:
   // read it as "hidden and never came back".  'tab_close' remains for browsers that skip the hide.
   CHECKOUT_LEFT: 'checkout_left',
   /** The traveller came back to a checkout they had backgrounded (`checkout_left` exitVia 'backgrounded'). */
   CHECKOUT_RESUMED: 'checkout_resumed',
   // NPT-2115 — the inline "add free cancellation" offer on checkout. Properties: hotelId,
   // fromBlockId, toBlockId, priceDelta.
   CHECKOUT_REFUNDABLE_UPGRADE_SHOWN: 'checkout_refundable_upgrade_shown',
   CHECKOUT_REFUNDABLE_UPGRADE_ACCEPTED: 'checkout_refundable_upgrade_accepted',
   CHECKOUT_REFUNDABLE_UPGRADE_DECLINED: 'checkout_refundable_upgrade_declined',
   /** The traveller went back from the refundable rate to the non-refundable one they arrived on. */
   CHECKOUT_REFUNDABLE_UPGRADE_REVERTED: 'checkout_refundable_upgrade_reverted',
   // NPT-2075 — the non-refundable acknowledgement was shown / blocked a submit.
   NONREFUNDABLE_ACK_SHOWN: 'nonrefundable_ack_shown',
   NONREFUNDABLE_ACK_BLOCKED: 'nonrefundable_ack_blocked',
   BOOKING_PAYMENT_METHOD_SELECTED: 'booking_payment_method_selected',
   RATE_EXPIRED_MODAL_SHOWN: 'rate_expired_modal_shown',
   RATES_UPDATED_MODAL_SHOWN: 'rates_updated_modal_shown',
   RATES_UPDATED_CONTINUED: 'rates_updated_continued',
   PRICE_CHANGED_MODAL_SHOWN: 'price_changed_modal_shown',
   PRICE_CHANGED_ACCEPTED: 'price_changed_accepted',
   PRICE_CHANGED_DECLINED: 'price_changed_declined',
   CHECKOUT_TIMER_EXPIRED: 'checkout_timer_expired',
   CONTINUE_CHECKOUT_MODAL_SHOWN: 'continue_checkout_modal_shown',
   CONTINUE_CHECKOUT_CLICKED: 'continue_checkout_clicked',

   // ─── Experiments / A/B tests ─────────────────────────────────────────
   // Exposure — fires the first time a user sees each variant per
   // browser session. Used as step 1 of every experiment funnel.
   EXPERIMENT_VIEWED: 'experiment_viewed',
   // Any sign-up CTA inside an experiment variant (card / modal / overlay).
   EXPERIMENT_CTA_CLICKED: 'experiment_cta_clicked',
   // User closed the card / modal / overlay (close button or backdrop).
   EXPERIMENT_DISMISSED: 'experiment_dismissed',
   // Emitted alongside the existing `signup_started` when the WorkOS
   // redirect was initiated from inside an experiment CTA. Use
   // super-props `last_experiment_key` / `last_experiment_variant` for
   // full-funnel attribution through to `signup_completed`.
   EXPERIMENT_SIGNUP_STARTED: 'experiment_signup_started',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

/**
 * Names of the substeps in the booking checkout flow. Keep the labels stable
 * across deployments — they become funnel steps in Mixpanel.
 */
export const BookingStepName = {
   CONTACT: 'contact',
   GUEST_NAME: 'guest_name',
   BILLING: 'billing',
   PROGRAM: 'program',
   PAYMENT: 'payment',
} as const;

export type BookingStepNameType = (typeof BookingStepName)[keyof typeof BookingStepName];
